import React, { useState } from 'react';
import { Card, MetricCard } from '../../components/design-system/Card';
import { Button } from '../../components/design-system/Button';
import { useAuth } from '../../hooks/useAuth';
import { clsx } from 'clsx';

interface WorkoutItem {
  id: string;
  name: string;
  completed: boolean;
  duration?: number;
}

interface AIInsight {
  message: string;
  question: string;
  actions: {
    accept: string;
    decline: string;
  };
}

export const EngagedDashboard: React.FC = () => {
  const { user } = useAuth();
  const [streak] = useState(13);
  const [showWidgetGallery, setShowWidgetGallery] = useState(false);
  
  const [todaysWorkouts] = useState<WorkoutItem[]>([
    { id: '1', name: 'Squats', completed: true, duration: 5 },
    { id: '2', name: 'Lunges', completed: false, duration: 5 },
    { id: '3', name: 'Planks', completed: false, duration: 3 },
  ]);

  const [progressMetrics] = useState({
    formScore: { value: 8.5, trend: 'up', change: '+0.5' },
    power: { value: 15, trend: 'up', change: '+15%' },
    consistency: { value: 87, trend: 'neutral', change: 'Steady' }
  });

  const [aiInsight] = useState<AIInsight>({
    message: 'Your form improved 23% this week. Ready for weighted squats?',
    question: 'Add weight progression?',
    actions: {
      accept: 'Yes, show me',
      decline: 'Not yet'
    }
  });

  const completedCount = todaysWorkouts.filter(w => w.completed).length;
  const progressPercentage = (completedCount / todaysWorkouts.length) * 100;

  const handleContinueWorkout = () => {
    // Navigate to next uncompleted workout
    const nextWorkout = todaysWorkouts.find(w => !w.completed);
    console.log('Continuing with:', nextWorkout?.name);
  };

  const handleAIResponse = (response: 'accept' | 'decline') => {
    console.log('AI response:', response);
    // Handle the user's choice
    if (response === 'accept') {
      // Show weighted workout options
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header with Streak */}
        <header className="flex items-center justify-between animate-fadeIn">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Welcome back, {user?.first_name || 'Athlete'}!
            </h1>
            <p className="text-gray-600 mt-1">
              {streak}-day streak 🔥 Keep it going!
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">
              {new Date().toLocaleDateString('en-US', { 
                weekday: 'long', 
                month: 'short', 
                day: 'numeric' 
              })}
            </p>
          </div>
        </header>

        {/* Two-Column Layout */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Today's Activities */}
          <Card className="animate-slideUp" style={{ animationDelay: '100ms' }}>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Today</h2>
            
            <div className="space-y-3">
              {todaysWorkouts.map((workout) => (
                <div
                  key={workout.id}
                  className={clsx(
                    'flex items-center justify-between p-3 rounded-lg transition-colors',
                    workout.completed 
                      ? 'bg-green-50 border border-green-200' 
                      : 'bg-gray-50 border border-gray-200'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={clsx(
                      'w-6 h-6 rounded-full flex items-center justify-center',
                      workout.completed 
                        ? 'bg-green-500 text-white' 
                        : 'bg-gray-300'
                    )}>
                      {workout.completed && (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className={clsx(
                      'font-medium',
                      workout.completed ? 'text-gray-700 line-through' : 'text-gray-900'
                    )}>
                      {workout.name}
                    </span>
                  </div>
                  {workout.duration && (
                    <span className="text-sm text-gray-500">{workout.duration} min</span>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-6">
              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={handleContinueWorkout}
                disabled={completedCount === todaysWorkouts.length}
              >
                {completedCount === todaysWorkouts.length ? 'All Done! 🎉' : 'Continue'}
              </Button>
            </div>
          </Card>

          {/* Progress Visualization */}
          <Card className="animate-slideUp" style={{ animationDelay: '150ms' }}>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Progress</h2>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Form Score</span>
                  <span className="font-semibold text-gray-900">
                    {progressMetrics.formScore.value}/10
                    <span className="text-green-600 text-xs ml-1">↗️ {progressMetrics.formScore.change}</span>
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Power Increase</span>
                  <span className="font-semibold text-gray-900">
                    {progressMetrics.power.change}
                  </span>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mt-4">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Today's Progress</span>
                  <span>{completedCount}/{todaysWorkouts.length} completed</span>
                </div>
                <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#4B5FDC] transition-all duration-500 ease-out"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
              </div>

              <button className="text-sm text-[#4B5FDC] hover:text-[#3A4BC9] transition-colors">
                View Details →
              </button>
            </div>
          </Card>
        </div>

        {/* AI Insights with Binary Choice */}
        <Card 
          variant="highlighted" 
          className="animate-slideUp" 
          style={{ animationDelay: '200ms' }}
        >
          <div className="flex items-start gap-4">
            <div className="text-2xl">🤖</div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-2">AI Insight</h3>
              <p className="text-gray-700 mb-4">{aiInsight.message}</p>
              
              <div className="flex gap-3">
                <Button
                  variant="primary"
                  size="md"
                  onClick={() => handleAIResponse('accept')}
                  haptic
                >
                  {aiInsight.actions.accept}
                </Button>
                <Button
                  variant="ghost"
                  size="md"
                  onClick={() => handleAIResponse('decline')}
                >
                  {aiInsight.actions.decline}
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Progressive Disclosure - Add Widgets */}
        <div className="text-center animate-slideUp" style={{ animationDelay: '250ms' }}>
          <button
            onClick={() => setShowWidgetGallery(true)}
            className="text-gray-500 hover:text-gray-700 transition-colors py-3 px-6 rounded-lg hover:bg-gray-100"
          >
            <span className="text-lg mr-2">+</span>
            Add widgets
          </button>
        </div>

        {/* Quick Metrics Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 animate-slideUp" style={{ animationDelay: '300ms' }}>
          <MetricCard
            label="Weekly Active Days"
            value="6/7"
            trend="up"
            trendValue="Better than 80% of users"
          />
          <MetricCard
            label="Total Workouts"
            value="42"
            trend="up"
            trendValue="+12 this month"
          />
          <MetricCard
            label="Avg. Form Score"
            value="8.5"
            trend="up"
            trendValue="Improving steadily"
          />
        </div>
      </div>

      {/* Widget Gallery Modal (placeholder) */}
      {showWidgetGallery && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Add Widgets</h3>
            <p className="text-gray-600 mb-4">Widget gallery coming soon...</p>
            <Button variant="ghost" onClick={() => setShowWidgetGallery(false)}>
              Close
            </Button>
          </Card>
        </div>
      )}
    </div>
  );
};

export default EngagedDashboard;